import React from 'react';
import { Input } from 'reactstrap';
import { matchSorter } from 'match-sorter';
import { Utils } from './';
import i18n from '../locales/i18n';
import _ from 'lodash';
import moment from 'moment';

class Filters { }

Filters.textFilter = ({ column: { filterValue, setFilter, preFilteredRows, id }}) =>
    <Input
        type="text"
        onChange={e => setFilter(e.target.value)}
    />;

Filters.groupsFilter = groups => ({ column: { filterValue, setFilter, preFilteredRows, id }}) =>
    groups ?
        <select
            className="form-control"
            onChange={event => setFilter(event.target.value)}
            style={{ width: "100%" }}
            value={filterValue}
        >
            <option key="0" value="">---</option>
            {(groups || []).map(g =>
                <option key={g.id} value={g.id}>{ g.name }</option>
            )}
        </select> : null;

/*
// This is a custom filter UI for selecting
// a unique option from a list
Filters.SelectColumnFilter = ({ column: { filterValue, setFilter, preFilteredRows, id }}) => {
    // Calculate the options for filtering
    // using the preFilteredRows
    const options = React.useMemo(() => {
        const options = new Set()
        preFilteredRows.forEach(row => {
            options.add(row.values[id])
        })
        return [...options.values()].sort()
    }, [id, preFilteredRows])

    // Render a multi-select box
    return (
        <select
            className="form-control"
            value={filterValue}
            onChange={e => {
                setFilter(e.target.value || undefined)
            }}
        >
            <option value="">All</option>
            {options.map((option, i) => (
                <option key={i} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
};

// This is a custom filter UI that uses a
// slider to set the filter value between a column's
// min and max values
Filters.SliderColumnFilter = ({ column: { filterValue, setFilter, preFilteredRows, id }}) => {
    // Calculate the min and max
    // using the preFilteredRows

    const [min, max] = React.useMemo(() => {
        let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        preFilteredRows.forEach(row => {
            min = Math.min(row.values[id], min)
            max = Math.max(row.values[id], max)
        })
        return [min, max]
    }, [id, preFilteredRows])

    return (
        <>
            <Input
                type="range"
                min={min}
                max={max}
                value={filterValue || min}
                onChange={e => {
                setFilter(parseInt(e.target.value, 10))
                }}
            />
            <Button onClick={() => setFilter(undefined)}>Off</Button>
        </>
    );
};

// This is a custom UI for our 'between' or number range
// filter. It uses two number boxes and filters rows to
// ones that have values between the two
Filters.NumberRangeColumnFilter = ({ column: { filterValue = [], preFilteredRows, setFilter, id }}) => {
    const [min, max] = React.useMemo(() => {
        let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        preFilteredRows.forEach(row => {
            min = Math.min(row.values[id], min)
            max = Math.max(row.values[id], max)
        })
        return [min, max]
    }, [id, preFilteredRows])

    return (
        <div
            style={{
            display: 'flex',
            }}
        >
            <input
                value={filterValue[0] || ''}
                type="number"
                onChange={e => {
                const val = e.target.value
                setFilter((old = []) => [val ? parseInt(val, 10) : undefined, old[1]])
                }}
                placeholder={`Min (${min})`}
                style={{
                width: '70px',
                marginRight: '0.5rem',
                }}
            />
            to
            <input
                value={filterValue[1] || ''}
                type="number"
                onChange={e => {
                const val = e.target.value
                setFilter((old = []) => [old[0], val ? parseInt(val, 10) : undefined])
                }}
                placeholder={`Max (${max})`}
                style={{
                width: '70px',
                marginLeft: '0.5rem',
                }}
            />
        </div>
    );
};*/

Filters.fuzzyTextFilterFn = (rows, id, filterValue) => {
    return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
};
// Let the table remove the filter if the string is empty
Filters.fuzzyTextFilterFn.autoRemove = val => !val

// Define a custom filter filter function!
Filters.filterGreaterThan = (rows, id, filterValue) => {
    return rows.filter(row => {
        const rowValue = row.values[id]
        return rowValue >= filterValue
    })
};
// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
Filters.filterGreaterThan.autoRemove = val => typeof val !== 'number'

Filters.rolesFilter = ({ column: { filterValue, setFilter, preFilteredRows, id }}) =>
    <select
        className="form-control"
        onChange={event => setFilter(event.target.value)}
        style={{ width: "100%" }}
        value={filterValue}
    >
        <option value="all">Tous</option>
        {Utils.roles().map((r) =>
            <option key={r.role} value={r.role}>{r.label}</option>)
        }
    </select>;

Filters.eventsFilter = (events_groups) => ({ filter, onChange }) => {
    const default_lang = Utils.default_lang();
    return <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="0" value="">---</option>
        {events_groups.map((g) => (
            <optgroup key={g.label} label={g.label}>{
                g.events.map((e) => <option key={e.id} value={e.id}>{e.title ? Utils.ucfirst(Utils.localized_or_fallback(e.title, default_lang)) : '---'}</option>)
            }
            </optgroup>
        ))}
    </select>;
}

Filters.daysFilter = ({ filter, onChange }) =>
    <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="0" value="">---</option>
        {Utils.days().map((p) =>
            <option key={p.index} value={p.index}>{i18n.t('common.' + p.label)}</option>
        )}
    </select>;

Filters.tktInstancesFilter = (tktInstances) => ({ filter, onChange }) =>
    <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="0" value="">---</option>
        {tktInstances && tktInstances.map((t) =>
            <option key={t.id} value={t.id}>{t.name}</option>
        )}
    </select>;

Filters.placesFilter = (places) => ({ filter, onChange }) => {
    places = _.sortBy(places, ['cinema', 'name'])
    return <select
               className="form-control"
               onChange={event => onChange(event.target.value)}
               style={{ width: "100%" }}
               value={filter ? filter.value : ""}
           >
               <option key="0" value="">---</option>
               {places && places.map((p) =>
                   <option key={p._id} value={p._id}>{`${(p.cinema === p.name || !p.cinema) ? p.name : p.cinema + " - " + p.name}`}</option>
               )}
           </select>;
}

Filters.categoriesFilter = (categories) => ({ filter, onChange }) => {
    const default_lang = Utils.default_lang();
    return <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="0" value="">---</option>
        {categories && categories.map((c) =>
            <option key={c._id} value={c._id}>{c.name[default_lang]}</option>
        )}
    </select>;
}

Filters.posFilter = (pos) => ({ filter, onChange }) => {
    const default_lang = Utils.default_lang();
    return <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="0" value="">---</option>
        {pos && pos.map((p) =>
            <option key={p._id} value={p._id}>{p.name[default_lang]}</option>
        )}
    </select>;
}

Filters.usersFilter = (users) => ({ filter, onChange }) =>
    <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="0" value="">---</option>
        {users && _.sortBy(users, (u) => u.fullname).map((u) => u && u.id &&
            <option key={u.id} value={u.id}>{u.fullname}</option>
        )}
    </select>;

Filters.activitiesFilter = (activities) => ({ filter, onChange }) => {
    const default_lang = Utils.default_lang();
    return <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="0" value="">---</option>
        {activities && activities.map((a) =>
            <option key={a.id} value={a.id}>{a.name[default_lang]}</option>
        )}
    </select>;
}

Filters.taskStatusFilter = () => ({ filter, onChange }) =>
    <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="0" value="">---</option>
        {Utils.taskStatus().map((s) =>
            <option key={s} value={s}>{i18n.t('tasks.status_' + s)}</option>
        )}
    </select>;

Filters.articleStockTypesFilter = (stock_types) => ({ filter, onChange }) =>
    <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="0" value="">---</option>
        {stock_types.map(s =>
            <option key={s} value={s}>{i18n.t('articles.stocks.type_' + s)}</option>
        )}
    </select>;

Filters.cashregisterClosingStatusFilter = () => ({ filter, onChange }) =>
    <select
        className="form-control"
        onChange={event => onChange(event.target.value)}
        style={{ width: "100%" }}
        value={filter ? filter.value : ""}
    >
        <option key="-1" value="">---</option>
        <option key="1" value={true}>{i18n.t('cashregisters.auto_closing_enabled')}</option>
        <option key="0" value={false}>{i18n.t('cashregisters.auto_closing_disabled')}</option>
    </select>;

Filters.textFilterMethod = (key) => (filter, rows) => {
    if (!rows)
        return rows;
    return matchSorter(rows, filter.value, { keys: [key] });
};

Filters.dateFilterMethod = () => (filter, row) => {
    return filter.value === "" ?
        true :
        row.filter((d) => moment(filter.value).isBetween(moment(d._original.start_at).format("YYYY[-]MM[-]DD"), moment(d._original.stop_at).format("YYYY[-]MM[-]DD"), "day", []))
}

Filters.usersActivitiesFilterMethod = (filter, row) => {
    return filter.value === "" ?
        true :
        row.activities.filter((a) => a.id === filter.value).length > 0;
}

Filters.eventsFilterMethod = (filter, rows) => matchSorter(rows, filter.value, { keys: ['events.0.id'] });
Filters.daysFilterMethod = (filter, rows) => matchSorter(rows, filter.value, { keys: ['days'] });
Filters.rolesFilterMethod = (filter, row) => filter.value === "all" ? true : row.role === filter.value;

export default Filters;
